/**
 * Position Interpolator - Smooth GPS Position Animation
 *
 * Provides smooth, realistic movement between GPS updates for live tracking displays.
 * Uses linear interpolation with configurable animation duration.
 */

export interface Position {
  lat: number;
  lng: number;
}

export type SnappingMode = 'none' | 'magnetic' | 'always';

export interface TrackerPosition {
  trackerId: string;
  currentPosition: Position;
  targetPosition: Position;
  startPosition: Position;
  startTime: number;
  duration: number;
  isAnimating: boolean;
  speed: number; // km/h - used to calculate realistic animation speed
  speedHistory: Array<{ speed: number; timestamp: number }>; // For moving average
  lastUpdateTime: number; // For speed validation
  snappingMode: SnappingMode; // Track which snapping mode this tracker uses
  routeDistance: number; // Current distance along route from start (km)
  isExtrapolating: boolean; // Whether currently extrapolating beyond last GPS point
  routePath: Position[] | null; // Cached route segment for this animation (for 'always' mode)
  baseRouteDistance: number; // Route distance at start of extrapolation (for accumulation)
}

export class PositionInterpolator {
  private trackerPositions: Map<string, TrackerPosition> = new Map();
  private animationFrameId: number | null = null;
  private updateCallback: ((positions: Map<string, Position>) => void) | null = null;

  /**
   * Default animation duration in milliseconds
   * GPS tracker settings: 10s interval when moving, 180s when stopped
   * Using 220% of interval (22s) to ensure continuous smooth movement
   * Animation is interrupted by next GPS update, preventing freeze gaps
   * This creates seamless movement for broadcast visualization
   */
  private readonly DEFAULT_DURATION = 22000; // 22 seconds (220% of 10s GPS interval)

  /**
   * GPS validation enabled (configurable via .env)
   * Set to false for TV production motorcycles that can exceed cycling speeds
   */
  private readonly VALIDATION_ENABLED =
    process.env.REACT_APP_GPS_VALIDATION_ENABLED !== 'false'; // Default: true

  /**
   * Maximum plausible speed for GPS validation (km/h)
   * Configurable via .env:
   * - Cycling: 150 km/h
   * - Motorcycles: 250 km/h
   * - Cars: 350 km/h
   */
  private readonly MAX_PLAUSIBLE_SPEED =
    parseInt(process.env.REACT_APP_GPS_MAX_SPEED || '150', 10);

  /**
   * Jump threshold in meters (configurable via .env)
   * If position change exceeds this, snap instead of interpolate (likely GPS correction)
   * Set to 300m to allow smooth interpolation for speeds up to ~100 km/h over 10s interval
   */
  private readonly JUMP_THRESHOLD =
    parseInt(process.env.REACT_APP_GPS_JUMP_THRESHOLD || '300', 10);

  /**
   * Moving average window in milliseconds (30 seconds)
   */
  private readonly SPEED_AVERAGE_WINDOW = 30000; // 30 seconds

  /**
   * Magnetic distance for route snapping (meters)
   * Only used in "magnetic" mode - snap to route if GPS within this distance
   */
  private readonly MAGNETIC_DISTANCE =
    parseInt(process.env.REACT_APP_ROUTE_SNAPPING_MAGNETIC_DISTANCE || '50', 10);

  /**
   * Route search window (km)
   * Search for closest route point within ±200m of expected position
   */
  private readonly ROUTE_SEARCH_WINDOW_KM = 0.2; // 200 meters

  /**
   * Route coordinates cache
   * Stores route coordinates to avoid repeated lookups
   */
  private routeCoordinatesCache: Position[] | null = null;

  /**
   * Set the callback function that receives interpolated positions
   */
  public setUpdateCallback(callback: (positions: Map<string, Position>) => void): void {
    this.updateCallback = callback;
  }

  /**
   * Set route coordinates for snapping
   * Call this when route changes to cache coordinates
   */
  public setRouteCoordinates(coordinates: Position[] | null): void {
    this.routeCoordinatesCache = coordinates;
  }

  /**
   * Calculate distance between two positions using Haversine formula
   * Returns distance in meters
   */
  private calculateDistance(pos1: Position, pos2: Position): number {
    const R = 6371000; // Earth's radius in meters
    const lat1 = pos1.lat * Math.PI / 180;
    const lat2 = pos2.lat * Math.PI / 180;
    const deltaLat = (pos2.lat - pos1.lat) * Math.PI / 180;
    const deltaLng = (pos2.lng - pos1.lng) * Math.PI / 180;

    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  /**
   * Calculate bearing (direction) from one point to another
   * Returns bearing in degrees (0-360)
   */
  private calculateBearing(from: Position, to: Position): number {
    const lat1 = from.lat * Math.PI / 180;
    const lat2 = to.lat * Math.PI / 180;
    const deltaLng = (to.lng - from.lng) * Math.PI / 180;

    const y = Math.sin(deltaLng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) -
              Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng);
    const bearing = Math.atan2(y, x) * 180 / Math.PI;

    return (bearing + 360) % 360; // Normalize to 0-360
  }

  /**
   * Calculate destination point given start point, bearing, and distance
   * Used for bearing-based extrapolation
   */
  private calculateDestination(start: Position, bearing: number, distanceMeters: number): Position {
    const R = 6371000; // Earth's radius in meters
    const bearingRad = bearing * Math.PI / 180;
    const lat1 = start.lat * Math.PI / 180;
    const lng1 = start.lng * Math.PI / 180;

    const lat2 = Math.asin(
      Math.sin(lat1) * Math.cos(distanceMeters / R) +
      Math.cos(lat1) * Math.sin(distanceMeters / R) * Math.cos(bearingRad)
    );

    const lng2 = lng1 + Math.atan2(
      Math.sin(bearingRad) * Math.sin(distanceMeters / R) * Math.cos(lat1),
      Math.cos(distanceMeters / R) - Math.sin(lat1) * Math.sin(lat2)
    );

    return {
      lat: lat2 * 180 / Math.PI,
      lng: lng2 * 180 / Math.PI
    };
  }

  /**
   * Validate if position change represents plausible speed
   * Returns true if valid, false if likely GPS glitch
   */
  private isPlausibleSpeed(oldPos: Position, newPos: Position, timeDiffMs: number): boolean {
    if (timeDiffMs <= 0) return true; // Can't validate without time

    const distance = this.calculateDistance(oldPos, newPos);
    const timeDiffHours = timeDiffMs / (1000 * 60 * 60);
    const impliedSpeed = (distance / 1000) / timeDiffHours; // km/h

    const isValid = impliedSpeed <= this.MAX_PLAUSIBLE_SPEED;

    if (!isValid) {
      console.warn(`GPS validation failed: Implied speed ${impliedSpeed.toFixed(1)} km/h exceeds max ${this.MAX_PLAUSIBLE_SPEED} km/h`);
    }

    return isValid;
  }

  /**
   * Check if position change exceeds jump threshold
   * Returns true if should snap (jump), false if should interpolate
   */
  private shouldJump(oldPos: Position, newPos: Position): boolean {
    const distance = this.calculateDistance(oldPos, newPos);
    return distance > this.JUMP_THRESHOLD;
  }

  /**
   * Calculate moving average speed from history
   * Returns average speed in km/h over the configured window
   */
  private getMovingAverageSpeed(speedHistory: Array<{ speed: number; timestamp: number }>): number {
    const now = Date.now();
    const cutoff = now - this.SPEED_AVERAGE_WINDOW;

    // Filter to recent speeds only
    const recentSpeeds = speedHistory.filter(entry => entry.timestamp >= cutoff);

    if (recentSpeeds.length === 0) return 0;

    const sum = recentSpeeds.reduce((acc, entry) => acc + entry.speed, 0);
    return sum / recentSpeeds.length;
  }

  /**
   * Update speed history and maintain window size
   */
  private updateSpeedHistory(
    history: Array<{ speed: number; timestamp: number }>,
    speed: number,
    timestamp: number
  ): Array<{ speed: number; timestamp: number }> {
    const cutoff = timestamp - this.SPEED_AVERAGE_WINDOW;

    // Add new speed
    const updated = [...history, { speed, timestamp }];

    // Remove old entries outside window
    return updated.filter(entry => entry.timestamp >= cutoff);
  }

  /**
   * Find closest point on route to GPS position
   * Uses optimized search within ±200m of expected route position
   *
   * @param gpsPosition Current GPS position
   * @param routeDistanceKm Expected distance along route (km) - used to optimize search
   * @returns Closest route point or null if no route available
   */
  private findClosestRoutePoint(
    gpsPosition: Position,
    routeDistanceKm: number
  ): Position | null {
    if (!this.routeCoordinatesCache || this.routeCoordinatesCache.length === 0) {
      return null;
    }

    const route = this.routeCoordinatesCache;

    // If routeDistanceKm is 0 or not provided, search entire route
    // This is slower but necessary when we don't know tracker's position on route
    if (routeDistanceKm === 0) {
      let closestPoint = route[0];
      let minDistance = this.calculateDistance(gpsPosition, closestPoint);

      // Search all route points (brute force, but accurate)
      for (let i = 1; i < route.length; i++) {
        const distance = this.calculateDistance(gpsPosition, route[i]);
        if (distance < minDistance) {
          minDistance = distance;
          closestPoint = route[i];
        }
      }

      return closestPoint;
    }

    // Optimization: Search only points near expected position (±200m along route)
    // This reduces search from potentially 10,000+ points to ~50 points
    let cumulativeDistance = 0;
    let searchStartIdx = 0;
    let searchEndIdx = route.length - 1;

    const searchWindowStart = Math.max(0, routeDistanceKm - this.ROUTE_SEARCH_WINDOW_KM);
    const searchWindowEnd = routeDistanceKm + this.ROUTE_SEARCH_WINDOW_KM;

    // Find start and end indices for search window
    for (let i = 0; i < route.length - 1; i++) {
      const segmentDistance = this.calculateDistance(route[i], route[i + 1]) / 1000; // km
      const nextCumulativeDistance = cumulativeDistance + segmentDistance;

      if (cumulativeDistance <= searchWindowStart && nextCumulativeDistance > searchWindowStart) {
        searchStartIdx = i;
      }

      if (cumulativeDistance <= searchWindowEnd && nextCumulativeDistance > searchWindowEnd) {
        searchEndIdx = i + 1;
        break;
      }

      cumulativeDistance = nextCumulativeDistance;
    }

    // Search for closest point within window
    let closestPoint = route[searchStartIdx];
    let minDistance = this.calculateDistance(gpsPosition, closestPoint);

    for (let i = searchStartIdx; i <= Math.min(searchEndIdx, route.length - 1); i++) {
      const distance = this.calculateDistance(gpsPosition, route[i]);
      if (distance < minDistance) {
        minDistance = distance;
        closestPoint = route[i];
      }
    }

    return closestPoint;
  }

  /**
   * Extract route segment between two distances along route
   * Used for route-following animation in 'always' snap mode
   *
   * @param startDistKm Start distance along route (km)
   * @param endDistKm End distance along route (km)
   * @returns Array of positions forming the route segment
   */
  private extractRouteSegment(startDistKm: number, endDistKm: number): Position[] | null {
    if (!this.routeCoordinatesCache || this.routeCoordinatesCache.length === 0) {
      return null;
    }

    const route = this.routeCoordinatesCache;
    const segment: Position[] = [];

    // Calculate cumulative distances to find segment bounds
    let cumulativeDistance = 0;
    let startIdx = 0;
    let endIdx = route.length - 1;
    let foundStart = false;

    for (let i = 0; i < route.length - 1; i++) {
      const segmentDist = this.calculateDistance(route[i], route[i + 1]) / 1000; // Convert to km
      cumulativeDistance += segmentDist;

      if (!foundStart && cumulativeDistance >= startDistKm) {
        startIdx = i;
        foundStart = true;
      }

      if (cumulativeDistance >= endDistKm) {
        endIdx = i + 1;
        break;
      }
    }

    // Extract segment (include extra points for smooth curves)
    const bufferPoints = 5; // Add buffer for smoother interpolation
    const actualStartIdx = Math.max(0, startIdx - bufferPoints);
    const actualEndIdx = Math.min(route.length - 1, endIdx + bufferPoints);

    for (let i = actualStartIdx; i <= actualEndIdx; i++) {
      segment.push(route[i]);
    }

    return segment.length > 0 ? segment : null;
  }

  /**
   * Find position on route at specific distance from start
   *
   * @param distanceKm Distance along route from start (km)
   * @returns Position at that distance, or null if not found
   */
  private findPositionOnRoute(distanceKm: number): Position | null {
    if (!this.routeCoordinatesCache || this.routeCoordinatesCache.length === 0) {
      return null;
    }

    const route = this.routeCoordinatesCache;
    let cumulativeDistance = 0;

    for (let i = 0; i < route.length - 1; i++) {
      const segmentDist = this.calculateDistance(route[i], route[i + 1]) / 1000; // Convert to km

      if (cumulativeDistance + segmentDist >= distanceKm) {
        // Distance falls within this segment - interpolate between points
        const remainingDist = distanceKm - cumulativeDistance;
        const progress = remainingDist / segmentDist;

        return {
          lat: route[i].lat + (route[i + 1].lat - route[i].lat) * progress,
          lng: route[i].lng + (route[i + 1].lng - route[i].lng) * progress
        };
      }

      cumulativeDistance += segmentDist;
    }

    // Distance beyond route - return last point
    return route[route.length - 1];
  }

  /**
   * Calculate route distance from start for a given position
   *
   * @param position GPS position
   * @returns Distance along route (km), or 0 if not on route
   */
  private calculateRouteDistanceForPosition(position: Position): number {
    if (!this.routeCoordinatesCache || this.routeCoordinatesCache.length === 0) {
      return 0;
    }

    const route = this.routeCoordinatesCache;

    // Find closest point on route
    let closestIdx = 0;
    let minDistance = this.calculateDistance(position, route[0]);

    for (let i = 1; i < route.length; i++) {
      const distance = this.calculateDistance(position, route[i]);
      if (distance < minDistance) {
        minDistance = distance;
        closestIdx = i;
      }
    }

    // Calculate cumulative distance to closest point
    let cumulativeDistance = 0;
    for (let i = 0; i < closestIdx; i++) {
      cumulativeDistance += this.calculateDistance(route[i], route[i + 1]) / 1000; // Convert to km
    }

    return cumulativeDistance;
  }

  /**
   * Snap GPS position to route based on snapping mode
   *
   * @param gpsPosition Raw GPS position
   * @param mode Snapping mode: 'none' | 'magnetic' | 'always'
   * @param routeDistanceKm Current distance along route (km) - for optimized search
   * @returns Snapped position (or original if mode='none' or no route available)
   */
  public snapToRoute(
    gpsPosition: Position,
    mode: SnappingMode,
    routeDistanceKm: number = 0
  ): Position {
    // Mode: none - no snapping, return raw GPS
    if (mode === 'none') {
      return gpsPosition;
    }

    // No route available - return raw GPS
    if (!this.routeCoordinatesCache || this.routeCoordinatesCache.length === 0) {
      return gpsPosition;
    }

    // Find closest point on route
    const closestPoint = this.findClosestRoutePoint(gpsPosition, routeDistanceKm);

    if (!closestPoint) {
      return gpsPosition;
    }

    // Mode: always - always snap to route regardless of distance
    if (mode === 'always') {
      return closestPoint;
    }

    // Mode: magnetic - snap only if within magnetic distance
    const distanceToRoute = this.calculateDistance(gpsPosition, closestPoint);

    if (distanceToRoute <= this.MAGNETIC_DISTANCE) {
      // Within magnetic zone - snap to route
      return closestPoint;
    } else {
      // Outside magnetic zone - use raw GPS (rider is off-route)
      return gpsPosition;
    }
  }

  /**
   * Update tracker's target position
   * This starts a smooth animation from current position to new position
   */
  public updateTrackerPosition(
    trackerId: string,
    newPosition: Position,
    speed?: number,
    snappingMode: SnappingMode = 'none',
    routeDistance?: number
  ): void {
    const existing = this.trackerPositions.get(trackerId);
    const now = Date.now();

    if (!existing) {
      // First time seeing this tracker - no animation needed
      this.trackerPositions.set(trackerId, {
        trackerId,
        currentPosition: newPosition,
        targetPosition: newPosition,
        startPosition: newPosition,
        startTime: now,
        duration: 0,
        isAnimating: false,
        speed: speed || 0,
        speedHistory: speed ? [{ speed, timestamp: now }] : [],
        lastUpdateTime: now,
        snappingMode,
        routeDistance: routeDistance || 0,
        isExtrapolating: false,
        routePath: null,
        baseRouteDistance: routeDistance || 0
      });
      return;
    }

    // VALIDATION 1: Check distance (how far the position moved)
    const distance = this.calculateDistance(existing.targetPosition, newPosition);

    // Check for duplicate position (WebSocket sends same GPS every 1s, but GPS updates every 10s)
    if (distance < 1) {
      // Position hasn't changed (< 1 meter) - ignore this update to avoid restarting animation
      console.debug(`Ignoring duplicate position for ${trackerId} (distance: ${distance.toFixed(2)}m)`);
      return;
    }

    // VALIDATION 2: Check if speed is plausible for the distance/time (if validation enabled)
    const timeDiff = now - existing.lastUpdateTime;
    let shouldSnap = distance > this.JUMP_THRESHOLD;

    if (this.VALIDATION_ENABLED) {
      const isSpeedPlausible = this.isPlausibleSpeed(existing.currentPosition, newPosition, timeDiff);

      // Decision logic:
      // - If distance > 100m AND speed is plausible → GPS correction (accept with snap)
      // - If distance > 100m AND speed is IMplausible → GPS glitch (reject)
      // - If distance < 100m AND speed is plausible → Normal movement (interpolate)
      // - If distance < 100m AND speed is IMplausible → GPS glitch (reject)

      if (!isSpeedPlausible) {
        // Speed is impossible - reject this update completely
        console.warn(`Rejecting GPS update for ${trackerId} - implausible speed detected (${distance.toFixed(1)}m in ${timeDiff}ms)`);
        return; // Keep current position, ignore this update
      }
    } else {
      console.debug(`Speed validation disabled - accepting all GPS updates for ${trackerId}`);
    }

    // If we reach here, the position is valid (either normal movement or GPS correction)

    // Update speed history for moving average
    const currentSpeed = speed !== undefined ? speed : existing.speed;
    const updatedSpeedHistory = this.updateSpeedHistory(
      existing.speedHistory,
      currentSpeed,
      now
    );

    // Get moving average speed for smoother animation
    const avgSpeed = this.getMovingAverageSpeed(updatedSpeedHistory);

    let duration = this.DEFAULT_DURATION;

    if (shouldSnap) {
      // Large jump detected BUT speed is plausible - likely GPS correction after signal loss
      console.log(`Large jump detected for ${trackerId} (${distance.toFixed(1)}m) - instant snap (GPS correction)`);
      duration = 0; // Instant snap
    } else if (avgSpeed < 1) {
      // Stopped - use 160s animation for 180s GPS interval when not moving
      duration = 160000; // 160 seconds (90% of 180s interval)
    } else if (avgSpeed > 50) {
      // Fast moving - use 20s for continuous smooth movement
      duration = 20000; // 20 seconds (200% of 10s interval)
    } else {
      // Normal movement - use 22s for continuous smooth movement
      duration = 22000; // 22 seconds (220% of 10s GPS interval)
    }

    // Calculate route path if in 'always' snap mode and route is available
    let routePath: Position[] | null = null;
    if (snappingMode === 'always' && this.routeCoordinatesCache && routeDistance !== undefined) {
      // TODO: Extract route segment for smooth route-following animation
      routePath = this.extractRouteSegment(existing.routeDistance, routeDistance);
    }

    // Start new animation from current interpolated position
    this.trackerPositions.set(trackerId, {
      trackerId,
      currentPosition: shouldSnap ? newPosition : existing.currentPosition, // Snap if jump
      targetPosition: newPosition,
      startPosition: shouldSnap ? newPosition : existing.currentPosition,
      startTime: now,
      duration,
      isAnimating: !shouldSnap, // Don't animate if snapping
      speed: currentSpeed,
      speedHistory: updatedSpeedHistory,
      lastUpdateTime: now,
      snappingMode,
      routeDistance: routeDistance || existing.routeDistance || 0,
      isExtrapolating: false, // New GPS update - exit extrapolation mode
      routePath,
      baseRouteDistance: routeDistance || existing.routeDistance || 0 // Reset base for potential future extrapolation
    });

    // Start animation loop if not already running
    if (!this.animationFrameId && !shouldSnap) {
      this.startAnimation();
    }
  }

  /**
   * Get current interpolated position for a tracker
   */
  public getTrackerPosition(trackerId: string): Position | null {
    const tracker = this.trackerPositions.get(trackerId);
    return tracker ? tracker.currentPosition : null;
  }

  /**
   * Get all current interpolated positions
   */
  public getAllPositions(): Map<string, Position> {
    const positions = new Map<string, Position>();
    this.trackerPositions.forEach((tracker, id) => {
      positions.set(id, tracker.currentPosition);
    });
    return positions;
  }

  /**
   * Get moving average speed for a tracker
   * Returns smoothed speed over the last 30 seconds
   * Use this for ETA calculations instead of instantaneous speed
   */
  public getTrackerAverageSpeed(trackerId: string): number | null {
    const tracker = this.trackerPositions.get(trackerId);
    if (!tracker) return null;

    return this.getMovingAverageSpeed(tracker.speedHistory);
  }

  /**
   * Start the animation loop
   */
  private startAnimation(): void {
    const animate = () => {
      const now = Date.now();
      let hasActiveAnimations = false;

      // Update all animating trackers
      this.trackerPositions.forEach((tracker, trackerId) => {
        if (tracker.isAnimating || tracker.isExtrapolating) {
          const elapsed = now - tracker.startTime;
          let progress = Math.min(elapsed / tracker.duration, 1.0);

          // Check if animation reached 100% - switch to extrapolation
          if (progress >= 1.0 && !tracker.isExtrapolating && tracker.speed > 1) {
            // Start extrapolation mode
            tracker.isExtrapolating = true;
            tracker.isAnimating = false;
            tracker.startTime = now; // Reset timer for extrapolation
            tracker.baseRouteDistance = tracker.routeDistance; // Save base route distance for extrapolation
            console.log(`${trackerId}: Starting extrapolation mode (speed: ${tracker.speed} km/h, baseDistance: ${tracker.baseRouteDistance.toFixed(2)}km)`);
          }

          if (tracker.isExtrapolating) {
            // EXTRAPOLATION MODE: Continue moving beyond last GPS point
            const extrapolationTime = now - tracker.startTime; // Time in extrapolation (ms)
            const speedMetersPerMs = (tracker.speed * 1000) / (3600 * 1000); // Convert km/h to m/ms
            const extrapolationDistance = extrapolationTime * speedMetersPerMs / 1000; // Convert to km

            // Calculate extrapolated position
            if (tracker.snappingMode === 'always' && this.routeCoordinatesCache) {
              // Route-based extrapolation - follow the route path
              // Always use baseRouteDistance as starting point, add accumulated extrapolation
              const extrapolatedRouteDistance = tracker.baseRouteDistance + extrapolationDistance;
              const extrapolatedPos = this.findPositionOnRoute(extrapolatedRouteDistance);

              if (extrapolatedPos) {
                tracker.currentPosition = extrapolatedPos;
                // DON'T update tracker.routeDistance here - it should stay at last GPS value
                // This prevents backward movement when duplicate GPS arrives
              }
            } else {
              // Bearing-based extrapolation - continue in last direction
              const bearing = this.calculateBearing(tracker.startPosition, tracker.targetPosition);
              tracker.currentPosition = this.calculateDestination(
                tracker.targetPosition,
                bearing,
                extrapolationDistance * 1000 // Convert back to meters
              );
            }

            hasActiveAnimations = true; // Keep animating
          } else {
            // NORMAL INTERPOLATION MODE
            const easedProgress = this.easeInOutQuad(progress);

            // Check if we should follow route path (always snap mode with route path)
            if (tracker.snappingMode === 'always' && tracker.routePath && tracker.routePath.length > 1) {
              // Route-following interpolation
              const routePathLength = tracker.routePath.length;
              const targetIdx = Math.min(
                Math.floor(easedProgress * (routePathLength - 1)),
                routePathLength - 2
              );
              const segmentProgress = (easedProgress * (routePathLength - 1)) - targetIdx;

              // Interpolate between route path points
              tracker.currentPosition = {
                lat: this.lerp(
                  tracker.routePath[targetIdx].lat,
                  tracker.routePath[targetIdx + 1].lat,
                  segmentProgress
                ),
                lng: this.lerp(
                  tracker.routePath[targetIdx].lng,
                  tracker.routePath[targetIdx + 1].lng,
                  segmentProgress
                )
              };
            } else {
              // Straight-line interpolation (normal mode)
              tracker.currentPosition = {
                lat: this.lerp(
                  tracker.startPosition.lat,
                  tracker.targetPosition.lat,
                  easedProgress
                ),
                lng: this.lerp(
                  tracker.startPosition.lng,
                  tracker.targetPosition.lng,
                  easedProgress
                )
              };
            }

            hasActiveAnimations = true;
          }

          this.trackerPositions.set(trackerId, tracker);
        }
      });

      // Notify callback with updated positions
      if (this.updateCallback) {
        this.updateCallback(this.getAllPositions());
      }

      // Continue animation loop if there are active animations
      if (hasActiveAnimations) {
        this.animationFrameId = requestAnimationFrame(animate);
      } else {
        this.animationFrameId = null;
      }
    };

    this.animationFrameId = requestAnimationFrame(animate);
  }

  /**
   * Linear interpolation between two values
   */
  private lerp(start: number, end: number, progress: number): number {
    return start + (end - start) * progress;
  }

  /**
   * Easing function for smooth acceleration and deceleration
   * https://easings.net/#easeInOutQuad
   */
  private easeInOutQuad(t: number): number {
    return t < 0.5
      ? 2 * t * t
      : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  /**
   * Remove a tracker from interpolation
   */
  public removeTracker(trackerId: string): void {
    this.trackerPositions.delete(trackerId);
  }

  /**
   * Clear all trackers and stop animation
   */
  public clear(): void {
    this.trackerPositions.clear();
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Stop all animations immediately (snap to target positions)
   */
  public stopAnimations(): void {
    this.trackerPositions.forEach((tracker, trackerId) => {
      if (tracker.isAnimating) {
        tracker.currentPosition = tracker.targetPosition;
        tracker.isAnimating = false;
        this.trackerPositions.set(trackerId, tracker);
      }
    });

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }
}

// Export singleton instance
export const positionInterpolator = new PositionInterpolator();
